// src/contexts/RoleMenusContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useAuth } from "../AuthContext";
import { fetchMenusForRole } from "../api/menuApi";
import { buildSidebarMenus } from "../utils/menuTransform";

const RoleMenusContext = createContext(null);

function getRoleCode(user) {
  const raw = user?.userType || user?.role || user?.ROLE || "";
  const r = String(raw).toUpperCase().trim();

  if (r === "AD" || r === "ADMIN") return "AD";
  if (r === "HT" || r === "HEADTEACHER") return "HT";
  if (r === "TE" || r === "TEACHER" || r === "TR") return "TE";
  if (r === "AC" || r === "ACCOUNTANT") return "AC";
  if (r === "OW" || r === "OWNER") return "OW";

  if (["AD", "HT", "TE", "AC", "OW"].includes(r)) return r;
  return "";
}

export function RoleMenusProvider({ children }) {
  const { user, token } = useAuth();

  const schoolId = user?.schoolId ?? user?.school_id ?? user?.school?.id ?? null;
  const roleCode = useMemo(() => getRoleCode(user), [user]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rawItems, setRawItems] = useState([]);
  const [sidebarMenus, setSidebarMenus] = useState([]);

  const refresh = useCallback(async () => {
    setError("");

    if (!schoolId || !roleCode) {
      setRawItems([]);
      setSidebarMenus([]);
      return;
    }

    setLoading(true);
    try {
      const items = await fetchMenusForRole({ schoolId, roleCode, token });
      setRawItems(items);
      setSidebarMenus(buildSidebarMenus(items));
    } catch (e) {
      setRawItems([]);
      setSidebarMenus([]);
      setError(e?.message || "Failed to load role menus.");
    } finally {
      setLoading(false);
    }
  }, [schoolId, roleCode, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      schoolId,
      roleCode,
      loading,
      error,
      rawItems,
      sidebarMenus,
      refresh,
    }),
    [schoolId, roleCode, loading, error, rawItems, sidebarMenus, refresh]
  );

  return <RoleMenusContext.Provider value={value}>{children}</RoleMenusContext.Provider>;
}

export function useRoleMenus() {
  const ctx = useContext(RoleMenusContext);
  if (!ctx) throw new Error("useRoleMenus must be used within RoleMenusProvider");
  return ctx;
}
